/**
 * @desc Application-wide `common` constants.
 * @example
 * import * as COMMON from 'constants/common';
 * console.log( COMMON.IS_USER_LOGGED_IN );
 */

// local storage key to check if user is logged in
export const IS_USER_LOGGED_IN = 'isUserLoggedIn';

// environments
export const ENVIRONMENTS = {
    PRODUCTION: 'production',
    DEVELOPMENT: 'development'
};

// placements for popover
export const PLACEMENTS = {
    AUTO: 'auto',
    AUTO_START: 'auto-start',
    AUTO_END: 'auto-end',
    TOP: 'top',
    TOP_START: 'top-start',
    TOP_END: 'top-end',
    BOTTOM: 'bottom',
    BOTTOM_START: 'bottom-start',
    BOTTOM_END: 'bottom-end',
    RIGHT: 'right',
    RIGHT_START: 'right-start',
    RIGHT_END: 'right-end',
    LEFT: 'left',
    LEFT_START: 'left-start',
    LEFT_END: 'left-end',
};

// HTTP methods
export const HTTP_METHODS = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE'
};

// http response status
export const HTTP_RESPONSE_STATUS = {
    NONE: 0,
    OK: 200,
    UNAUTHORIZED: 401,
    FORBIDDEN: 401,
};

// service response status
export const SERVICE_RESPONSE_STATUS = {
    SUCCESS: 'success',
    FAILURE: 'failure',
    CANCELLED: 'cancelled',
};

// target variables for links
export const TARGET = {
    SELF: '_self',
    OUTSIDE: '_blank'
};

// blank symbol value
export const BLANK_VALUE_SYMBOL = '-';

// sizes
export const SIZES = {
    EXTRA_SMALL: 'extra-small',
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
    EXTRA_LARGE: 'extra-large',
};

// sort directions
export const SORT_DIRECTIONS = {
    ASCENDING: 'ascending',
    DESCENDING: 'descending',
};

// global labels
export const GLOBAL_LABELS = {
    DISPLAY_TEXT_MORE: 'displayTextMore',
    DISPLAY_TEXT_ERROR: 'displayTextError',
    DISPLAY_TEXT_DONE: 'displayTextDone',
    DISPLAY_TEXT_RETRY: 'displayTextRetry',
    DISPLAY_TEXT_PREV: 'displayTextPrev',
    DISPLAY_TEXT_NEXT: 'displayTextNext',
    DISPLAY_TEXT_CLOSE: 'displayTextClose',
    DISPLAY_TEXT_SELECT: 'displayTextSelect',
    DISPLAY_TEXT_NO_MATCH_FOUND: 'displayTextNoMatchFound',
    DISPLAY_TEXT_NO_DATA_FOUND: 'displayTextNoDataFound',
    DISPLAY_TEXT_SOMETHING_WENT_WRONG: 'displayTextSomethingWentWrong',
    DISPLAY_TEXT_TRY_AGAIN_LATER: 'displayTextTryAgainLater',
    DISPLAY_TEXT_AND_LOWER_CASE: 'displayTextAndLowerCase',
    DISPLAY_TEXT_CALLER: 'displayTextCaller',
    DISPLAY_TEXT_CALLEE: 'displayTextCallee',
    DISPLAY_TEXT_TYPE_TO_SEARCH: 'displayTextTypeToSearch',
    DISPLAY_TEXT_CONFIRMATION: 'displayTextConfirmation',
    DISPLAY_TEXT_ARE_YOU_SURE: 'displayTextAreYouSure',
    DISPLAY_TEXT_YES: 'displayTextYes',
    DISPLAY_TEXT_NO: 'displayTextNo',
    DISPLAY_TEXT_OK: 'displayTextOk',
    DISPLAY_TEXT_SUCCESS_TRANSFER_CALL: 'displayTextSuccessTransferCall',
    DISPLAY_TEXT_DONE_SUCCESSFULLY: 'displayTextDoneSuccessfully',
    DISPLAY_TEXT_PAYMENT_ERROR_CARD_EXISTS: 'displayTextPaymentErrorCardExists',
    DISPLAY_TEXT_PAYMENT_ERROR_GENERAL: 'displayTextPaymentErrorGeneral',
    DISPLAY_TEXT_EXP_DATE: 'displayTextExpDate',
    DISPLAY_TEXT_LAST_FOUR_DIGITS_OF_CREDIT_CARD: 'displpayTextLastFourDigitsOfCreditCard',
    DISPLAY_TEXT_ALL: 'displayTextAll',
    DISPLAY_TEXT_DRAG_FILES: 'displayTextDragFilesToUploadOr',
    DISPLAY_TEXT_BROWSE: 'displayTextBrowse',
    DISPLAY_TEXT_ERROR_CARD_TYPE_NOT_FOUND: 'displayTextPaymentErrorCardTypeNotFound',
    DISPLAY_TEXT_DONT_HAVE_PERMISSIONS: 'displayTextDontHavePermissions',
    DISPLAY_TEXT_COPY: 'displayTextCopyText'
};

// global errors
export const GLOBAL_ERRORS = {
    PAYMENT_CARD_EXISTS: 'paymentCardExists',
    PAYMENT_GENERAL: 'paymentGeneral',
    CARD_TYPE_NOT_FOUND: 'cardTypeNotFound'
};

// field and table-column types
export const DATA_TYPES = {
    TEXT: 'text',
    TEXTAREA: 'textarea',
    NUMBER: 'number',
    DATE: 'date',
    PHONE: 'phone',
    EMAIL: 'email',
    ORG: 'org', // organization with `name` and `code`
    ADDRESS: 'address',
    SINGLE_SELECT: 'singleSelect',
    SINGLE_SELECT_STATIC_DESC: 'singleSelectWithStaticDesc',
    MULTI_SELECT: 'multiSelect',
    DEEP_SELECT: 'deepSelect',
    DEEP_MULTI_SELECT: 'deepMultiSelect',
    CURRENCY: 'currency',
    RADIO: 'radio',
    RADIO_LIST: 'radioList',
    SEARCH: 'search', // async single select
    SEARCH_SINGLE_SELECT: 'singleSelectSearch', // async single select (same as above)
    SEARCH_MULTI_SELECT: 'multiSelectSearch', // async multi select
    ARRAY: 'array',
    STEP_PROGRESS: 'stepProgress',
    MOOD: 'mood',
    DISTANCE: 'distance',
    DESCRIPTION: 'description',
    TIME: 'time',
    RADIO_FIELD: 'radioField',
    DATE_TIME_SELECT: 'dateTimeSelect',
    DRUG_INTAKE_SELECT: 'drugIntakeSelect',
    REJECT_SELECT: 'rejectSelect',
    MULTI_SELECT_FIELD: 'multiSelectField',
    DOB: 'dob',
    FORM_SECTION: 'formSection',
    SECTION_TITLE: 'sectionTitle',
    INPUT_FIELD_CHECKBOX: 'inputFieldCheckbox',
    TEXT_WITH_INFO: 'textWithInfo'
};

export const FORM_NAME = {
    CLAIM_TO_DO: 'CLAIM_TO_DO'
};

export const PHARMACY_ID_TYPE = {
    NPI: 'NPI',
    NABP: 'NABP'
};

// field validation names
export const DATA_VALIDATIONS = {
    REQUIRED: 'required', // field is required
    TYPE_NUMBERS: 'data_number', // field's data is a number
    TYPE_INTEGERS: 'data_integer', // field's data is an integer
    VALUE_ET: 'value_et', // field value is equal to x
    VALUE_GT: 'value_gt', // field value is greater than x
    VALUE_GTE: 'value_gte', // field value is greater than or equal to x
    VALUE_LT: 'value_lt', // field value is less than x
    VALUE_LTE: 'value_lte', // field value is less than or equal to x
    SEARCH: 'search',
    ARRAY: 'array',
    MAX_LENGTH: 'maxlength',
    ZIPCODE: 'zipcode',
    US_PHONE: 'usPhone',
    NOT_EMPTY: 'notEmpty',
    EMAIL: 'email',
    TIME: 'time',
    ALPHANUMERIC: 'alphanumeric',
    EXACT_LENGTH: 'exactlength'
};

// grouping options type for `groupOptionsByType` utility
export const YEAR = 'year';
export const AUTO = 'auto';
export const GROUP_OPTION = 'groupOption';
export const ALL = 'all';

// `selectAll` option name and value
export const SELECT_ALL_OPTION_VALUE = 'ALL';
export const NO_DATA_AVAILABLE_MESSAGE = 'No Data Available';
export const OR  = 'OR';
export const ITEMS_LEVEL = 'itemsLevel';

// widget error types
export const WIDGET_ERROR_TYPE = {
    DEFAULT: 'default',
    NO_DATA_FOUND: 'noDataFound',
    NO_MATCH_FOUND: 'noMatchFound',
};

// platforms
export const DEVICE_PLATFORMS = {
    __PREFIX__: 'mode--platform',
    DEFAULT: 'default',
    MOBILE: 'mobile',
    BROWSER: 'browser',
    ANDROID_BROWSER: 'android-browser',
    IOS_BROWSER: 'ios-browser',
    APP: 'app',
    ANDROID_APP: 'android-app',
    IOS_APP: 'ios-app',
};

// status-bar themes
export const STATUS_BAR_THEME = {
    LIGHT: 'light',
    DARK: 'dark',
};

// call activity state
export const CALL_ACTIVITY_STATE = {
    INACTIVE: 'inactive',
    AUTHORIZING: 'authorizing',
    VERIFYING: 'verifying',
    ACTIVE: 'active',
    PAUSED: 'paused',
    ENDED: 'ended'
};

// caller type names
export const CALLER_TYPE_NAMES = {
    MEMBER: 'member',
    PHARMACY: 'pharmacy',
    DOCTOR: 'doctor',
    THIRD_PARTY: 'thirdParty',
    CLIENT_REPRESENTATIVE: 'clientRepresentative',
};

// page-level preloader templates
export const PAGE_LEVEL_PRELOADER_TEMPLATES = {
    DEFAULT: 'DEFAULT',
    NANOBAR: 'NANOBAR',
};

// CRUD operations
export const CRUD_OPERATION = {
    CREATE: 'create',
    READ: 'read',
    UPDATE: 'update',
    DELETE: 'delete',
    ACTIVATE: 'activate',
    INACTIVATE: 'inactivate',
};

// table widget template types
export const TABLE_TEMPLATE_TYPE = {
    DEFAULT: 'default',
    INLINE: 'inline',
    ADVANCED_SEARCH: 'advanced_search'
};

// family relationship
export const FAMILY_RELATIONSHIP = {
    NOT_SPECIFIED: 'notSpecified',
};

// coverage status
export const COVERAGE_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
};

// row view table
export const ROW_VIEW_TEMPLATE_TYPE = {
    DEFAULT: 'default',
    BLOCK: 'block',
};

// masks
export const MASKS = {
    usPhone: '999-999-9999',
    time: '99:99'
};

// call tagging modes
export const CALL_TAGGING_MODES = {
    AUTO: 'auto',
    MANUAL: 'manual',
};

// call resolution status
export const CALL_RESOLUTION_STATUS = {
    RESOLVED: 'resolved',
    UNRESOLVED: 'unresolved',
};


export const ORIGIN = {
    MEMBER_MANAMGENT: 'memberManagement',
    ORDER_DETAIL: 'orderDetail',
    RX_DETAIL: 'rxDetail'
};

export const WIDGET_ERROR_TEMPLATE = {
    DEFAULT: 'default',
    WARNING: 'warning',
    TO_DO_OUTBOUND_CALL_FAILED: 'todoOutboundCallFailed'
};

export const ERROR_RESPONSE_TEMPLATE = {
    DEFAULT: 'default',
    notification: 'notification'
};

// Administration status
export const ADMINISTRATION_STATUS = {
    ENABLED: 'enabled',
    DISABLED: 'disabled',
};

export const MIME_TYPES = {
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.pdf': 'application/pdf',
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
};

// file uploader errors
export const FILE_UPLOADER_ERRORS = {
    INVALID_FILE: 'INVALID_FILE',
    INVALID_FILE_DIMENSIONS: 'INVALID_FILE_DIMENSIONS',
    INVALID_ASPECT_RATIO: 'INVALID_ASPECT_RATIO',
    SERVER_ERROR: 'SERVER_ERROR',
};

export const DATE_TIME_INFO_FORMAT = 'MMM DD YYYY, hh:mm:ss.sssa';
export const CANCELLED_ORDER_STATUS = 'CANCELLED';
export const CLINIC_NUMBER = 'CLINIC_NUMBER';

export const ATTESTATION_PROVIDER = {
    CLINIC: 'clinic',
    PATIENT: 'patient',
    CAREGIVER: 'caregiver'
};

export const FORM_FIELDS = {
    CATEGORY: 'category',
    CATEGORY_TYPE: 'categoryType',
    CATEGORY_SUB_TYPE: 'categorySubType',
    CATEGORY_SUB_TYPE_DETAIL: 'categorySubTypeDetail',
    GRIEVANCE_MODE: 'grievanceMode',
    INQUIRY_MODE: 'inquiryMode',
    PO_BOX: 'poBox'
};

export const INQUIRY_MODE = {
    ORAL: 'Oral'
};

export const MODE_TYPE = {
    PHONE_REQUEST: 'Phone Request'
};

export const TABLE_COLUMN = {
    ELIGIBILITY_STATUS: 'eligibilityStatus'
};

export const ELIGIBILITY_STATUS = {
    INACTIVE: 'inactive',
    TERMED: 'termed',
    HOLD: 'hold'
};

export const NAVIGATION_TABS_TYPE = {
    SECONDARY: 'secondary'
};

export const CONTACT_TYPE = {
    EMAIL: 'email'
};

export const ADDRESS_SELECTION_TYPE = {
    RECOMMENDED: 'recommended',
    SUBMITTED: 'submitted'
};

export const ACTIONS = {
    NEXT: 'next',
    BACK: 'back'
};

export const ALERTS_LISTING_ACTIONS = {
    NAV_ACTION: 'navAction',
    FILTER_ACTION: 'filterAction',
    ACCORDIAN_ACTION: 'accordianAction',
    ALERT_ACTION: 'alertAction',
    ALERT_TEXT_ACTION: 'alertTextAction'
};

export const ALERT_ACTIONS = {
    SHOW_LATER: 'showLater'
};

export const ALERT_TEXT_ACTIONS = {
    REDIRECT: 'redirect'
};

export const ALERT_FILTERS = {
    ALL: 'all',
    CRITICAL: 'critical',
    OTHER: 'other'
};

export const GLOBAL_URLS = {
    START_CALCULATING_ALERTS: 'startCalculatingAlerts'
};

export const FLYOUT_TEMPLATE = {
    DEFAULT: 'default',
    WEBSOCKET_DEBUG: 'websocket_debug'
};

export const ACCESS_MANAGEMENT_TABS = {
    USER_GROUPS: 'userGroups',
    PERMISSIONS: 'permissions'
};

export const ACCESS_MANAGEMENT_USER_GROUPS_MODE = {
    LIST: 'list',
    CREATE: 'create',
    EDIT: 'edit'
};

export const CREATE_USER_GROUP_TABS = {
    USER_GROUP_TAB: 'userGroupTab',
    USERS_TAB: 'usersTab'
};
